/// <amd-module name="Wit/MentionsAutocomplete/WorkItemMentionPreview" />

import { ITfsPageService } from "Tfs/Platform/Page";
import { IObservableValue } from "VSS/Core/Observable";
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { IVssLocationService } from "VSS/Platform/Location";
import { Observer } from "VSSUI/Observer";
import { WorkItemState } from "Wit/UI/Components/WorkItemState/WorkItemState";
import { WorkItemStateProvider, WorkItemTrackingDataProvider, WorkItemTypeIconProvider } from "Wit/UI/Data";
import { WorkItemTypeIcon } from "Wit/UI/WorkItemTypeIcon";
import { IWorkItemDataProviderPayload } from "Wit/WorkItemDataManager/Contracts";
import * as React from "react";
import { IWorkItemProviderItem } from "./Providers/IWorkItemProviderItem";

interface IWorkItemMentionPreviewProps {
    workItemId: string;
    workItemMention: IWorkItemProviderItem | IObservableValue<IWorkItemProviderItem | undefined>;
    pageContext: IVssPageContext;
    onMentionClick: (workItemMention: IWorkItemProviderItem) => void;
    dataProvider: WorkItemTrackingDataProvider;
}

export function WorkItemMentionPreview({ workItemId, workItemMention, pageContext, dataProvider, onMentionClick }: IWorkItemMentionPreviewProps) {
    return (
        <Observer workItemMention={workItemMention}>
            {({ workItemMention }: { workItemMention: IWorkItemProviderItem | undefined }) => {
                const locationService = pageContext.getService<IVssLocationService>("IVssLocationService");
                if (workItemMention) {
                    const mentionUrl = locationService.routeUrl("ms.vss-work-web.work-items-form-route", {
                        id: `${workItemMention.id}`,
                        project: workItemMention.projectName
                    });

                    return (
                        <span className="margin-left-4" onClick={() => onMentionClick(workItemMention)}>
                            <WorkItemTypeIconProvider
                                dataProvider={dataProvider}
                                dataSource={{
                                    projectIdOrName: workItemMention.projectName,
                                    workItemTypeName: workItemMention.workItemType
                                }}
                            >
                                {colorAndIcon => (
                                    <a
                                        data-wi={JSON.stringify([workItemMention, mentionUrl])}
                                        className="mention-link mention-widget-workitem inline-flex-row mention-wi-link body-m"
                                        href={mentionUrl}
                                        target="_blank"
                                        style={{ borderLeftColor: `#${colorAndIcon.color}` }}
                                    >
                                        <WorkItemTypeIcon data={colorAndIcon} className="work-item-icon" suppressTooltip={true} />
                                        <span className="secondary-text mention-workitem-id-no-flex">{workItemMention.id}</span>
                                        <span className="mention-widget-workitem-title fontWeightSemiBold">{workItemMention.title}</span>
                                        <WorkItemStateProvider
                                            dataProvider={dataProvider}
                                            dataSource={{
                                                projectIdOrName: workItemMention.projectName,
                                                workItemTypeName: workItemMention.workItemType,
                                                stateName: workItemMention.state
                                            }}
                                        >
                                            {stateAndColor => (
                                                <>
                                                    <span className="mention-widget-workitem-state-divider flex-self-center" />
                                                    <WorkItemState data={stateAndColor} className="mention-widget-workitem-state" />
                                                </>
                                            )}
                                        </WorkItemStateProvider>
                                    </a>
                                )}
                            </WorkItemTypeIconProvider>
                        </span>
                    );
                } else {
                    const pageService = pageContext.getService<ITfsPageService>("ITfsPageService");
                    const pageData = pageService.getData();
                    let projectName = pageData?.project?.name ?? "";
                    if (projectName === "") {
                        const dataProviderRequestProperties = {
                            id: parseInt(workItemId)
                        };
                        const wit = pageContext
                            .getService<IVssContributionService>("IVssContributionService")
                            .getData<IWorkItemDataProviderPayload>("ms.vss-work-web.work-item-data-provider", dataProviderRequestProperties);
                        const projects = wit?.["work-item-project-field-data"]?.data.projects;
                        if (projects?.length) {
                            projectName = projects[0].name ?? "";
                        }
                    }
                    const mentionUrl = locationService.routeUrl("ms.vss-work-web.work-items-form-route", {
                        id: workItemId,
                        project: projectName
                    });
                    return (
                        <a
                            data-wi={mentionUrl}
                            href={mentionUrl}
                            className="mention-link mention-wi-link mention-widget-workitem-no-access margin-left-4"
                        >
                            #{workItemId}
                        </a>
                    );
                }
            }}
        </Observer>
    );
}
